import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, XIcon } from 'lucide-react';
import { repertoire, repertoireFilters, RepertoireCategory } from '../../data/repertoire';
type Filter = 'all' | RepertoireCategory;
export function RepertoireBrowser({
  initialQuery = ''
}: {
  initialQuery?: string;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [filter, setFilter] = useState<Filter>('all');
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return repertoire.filter(e => filter === 'all' || e.category === filter).map(e => {
      if (!q || e.artist.toLowerCase().includes(q)) return e;
      const songs = e.songs.filter(s => s.toLowerCase().includes(q));
      return songs.length ? {
        ...e,
        songs
      } : null;
    }).filter((e): e is NonNullable<typeof e> => e !== null).sort((a, b) => a.artist.localeCompare(b.artist, 'nl'));
  }, [query, filter]);
  const grouped = useMemo(() => {
    const map = new Map<string, typeof results>();
    results.forEach(e => {
      const letter = /^[a-z]/i.test(e.artist) ? e.artist[0].toUpperCase() : '#';
      map.set(letter, [...(map.get(letter) ?? []), e]);
    });
    return Array.from(map.entries());
  }, [results]);
  const songCount = results.reduce((n, e) => n + e.songs.length, 0);
  return <div data-id="element-210" data-mp="1hqwpfmbie91r">
      <div className="sticky top-16 z-30 -mx-5 flex flex-col gap-4 border-y border-line bg-ink/95 px-5 py-4 backdrop-blur md:top-20 md:-mx-10 md:flex-row md:items-center md:justify-between md:px-10" data-id="element-211" data-mp="1hr51jbzt5abw">
        <div role="tablist" aria-label="Filter repertoire" className="flex flex-wrap gap-2" data-id="element-212" data-mp="17x61qjn7i2n4">
          {repertoireFilters.map(f => <button key={f.id} role="tab" aria-selected={filter === f.id} onClick={() => setFilter(f.id)} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber ${filter === f.id ? 'bg-paper text-ink' : 'text-sand hover:text-paper'}`} data-id="element-213" data-mp="yz3x7u49krv3">
              {f.label}
            </button>)}
        </div>
        <label className="relative block md:w-80" data-id="element-214" data-mp="5fmrg520exd1">
          <span className="sr-only" data-id="element-215" data-mp="ad681bm6jm85">Zoek artiest of nummer</span>
          <SearchIcon aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" data-id="element-216" data-mp="adocgvizx6r5" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Zoek artiest of nummer" className="w-full rounded-full border border-line bg-stage py-3 pl-11 pr-10 text-sm text-paper placeholder:text-muted focus:border-amber focus:outline-none" data-id="element-217" data-mp="1323nl6z0b4x7" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Zoekopdracht wissen" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted hover:text-paper" data-id="element-218" data-mp="1gzs0nsmu17rp">
              <XIcon className="h-4 w-4" data-id="element-219" data-mp="31j44mhf5ayob" />
            </button>}
        </label>
      </div>

      <p aria-live="polite" className="mt-8 text-sm text-muted" data-id="element-220" data-mp="3g7i72r8ngl4c">
        {songCount} {songCount === 1 ? 'nummer' : 'nummers'} van {results.length} {results.length === 1 ? 'artiest' : 'artiesten'}
      </p>

      {results.length === 0 ? <div className="py-24 text-center" data-id="element-221" data-mp="2nlz0wo2wmbw8">
          <p className="font-serif text-4xl text-paper" data-id="element-222" data-mp="2im2z8vhoiwju">Geen resultaten voor “{query}”</p>
          <p className="mt-4 text-sand" data-id="element-223" data-mp="2wl2mpztgh5ef">
            Geen nood — veel nummers studeer ik graag voor u in.{' '}
            <Link to="/contact?type=optreden" className="text-amber underline underline-offset-4" data-id="element-224" data-mp="2si5wpo1l670g">
              Vraag het aan
            </Link>
          </p>
        </div> : <div className="mt-6 space-y-2" data-id="element-225" data-mp="7trodilpq72k">
          {grouped.map(([letter, entries]) => <section key={letter} aria-label={`Artiesten met ${letter}`} className="grid grid-cols-1 gap-4 border-t border-line py-8 md:grid-cols-12" data-id="element-226" data-mp="133ogi02dzvza">
              <p aria-hidden className="font-serif text-6xl leading-none text-amber md:col-span-2" data-id="element-227" data-mp="1751oogsycb9d">
                {letter}
              </p>
              <ul className="columns-1 gap-10 sm:columns-2 md:col-span-10 lg:columns-3" data-id="element-228" data-mp="1h3h4hnmj3y3k">
                {entries.map(e => <li key={e.artist} className="mb-6 break-inside-avoid" data-id="element-229" data-mp="1m197bcopp4mj">
                    <p className="text-sm font-semibold text-paper" data-id="element-230" data-mp="1q34aynu47u3h">{e.artist}</p>
                    <ul className="mt-1 space-y-0.5" data-id="element-231" data-mp="1uum8t30udek8">
                      {e.songs.map(s => <li key={s} className="text-[15px] text-sand" data-id="element-232" data-mp="1zt5xvcaqf6gt">
                          {s}
                        </li>)}
                    </ul>
                  </li>)}
              </ul>
            </section>)}
        </div>}
    </div>;
}